import { Component, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { FormControl } from "@angular/forms";

import { UserPermissions } from "./../../../../models/domains/pbm/user/user-permissions";
import { UserService } from "src/app/ui/services/user.service";
import { CompanyService } from "src/app/ui/services/pbm/company.service";
import { LoadingService } from "src/app/ui/services/loading.service";
import { PageableResult } from "src/app/ui/models/domains/paginator/pageable-result";
import { PagingRequest } from "src/app/ui/models/domains/paginator/paging-request";
import { MaskInput } from "src/app/ui/models/domains/masks/mask.model";
import { AppContext } from "src/app/ui/contexts/app-context";
import { FeatureViewService } from "src/app/ui/services/feature-view.service";
import { FeatureViews } from "src/app/ui/models/domains/features/custom-feature-views";
import { PageViews } from "src/app/ui/models/domains/features/custom-page-views";
import { RoutePath } from "src/app/ui/models/domains/route-paths.model";
import { UserProfile } from "src/app/ui/models/domains/pbm/user/user-profile";
import { ICompanyFilterParams } from "src/app/ui/models/domains/pbm/companies/company-filter-params";

@Component({
  selector: "search-company",
  templateUrl: "./search-company.component.html",
  styleUrls: ["./search-company.component.scss"],
})
export class SearchCompanyComponent implements OnInit {
  filterText: string = "";
  pageableResult: PageableResult = new PageableResult();
  fiscalNumberMask = MaskInput.getFicalNumberMask();
  searchType = new FormControl(1);
  searchStatus = new FormControl(3);
  statusPreference: number = 3;

  private searchCompanyFirst: boolean = true;
  private userProfile: UserProfile;

  constructor(
    private router: Router,
    private featureViewService: FeatureViewService,
    private loadingService: LoadingService,
    private companyService: CompanyService,
    private appContext: AppContext,
    private userService: UserService,
  ) {}

  ngOnInit(): void {
    this.featureViewService.activeFeatureView(FeatureViews.CompanySearchFeatureView, PageViews.CompanyPageView);
    this.userProfile = this.userService.getUserProfile();
    const savedFilter = this.userProfile.getPreferences().getFilterText() || "";
    this.searchCompanies(savedFilter);
  }

  // --- Navegação ---
  navigateToEditCompany(fiscalNumber: string): void {
    const cleanFiscalNumber = fiscalNumber.replace(/[./-]/g, "");
    this.router.navigate([RoutePath.EditCompany, cleanFiscalNumber]);
  }

  scrollToLastCompany(): void {
    const bottom = document.getElementById("bottom");
    bottom?.scrollIntoView();
  }

  // --- Busca de Empresas ---
  onPressEnterSearchCompanies(event: KeyboardEvent): void {
    if (event.key === "Enter") {
      this.statusPreference = this.searchStatus.value;
      this.searchCompanies(this.filterText);
    }
  }

  searchCompanies(filterText: string): void {
    this.statusPreference = this.searchStatus.value;
    this.searchCompanyFirst = true;
    this.doSearchCompany(1, this.appContext.getTotalPerPage(), filterText);
  }

  searchMoreCompanies(): void {
    this.searchCompanyFirst = false;
    const start = this.pageableResult.getEnd() + 1;
    this.doSearchCompany(start, this.appContext.getTotalPerPage(), this.userProfile.getPreferences().getFilterText());
  }

  private doSearchCompany(start: number, end: number, filterText: string): void {
    const ref = this.loadingService.beginLoading();
    const pagingRequest = new PagingRequest();
    pagingRequest.setStart(start);
    pagingRequest.setEnd(end);

    const filterParams: ICompanyFilterParams = {
      filterText: this.cleanFilterText(filterText),
      status: this.statusPreference,
    };

    this.companyService
      .searchCompany(filterParams, pagingRequest)
      .then((result) => {
        this.pageableResult.setStart(start);
        this.pageableResult.setEnd(end);
        this.pageableResult.setTotalItems(result.getTotalItems());
        if (this.searchCompanyFirst) {
          this.pageableResult.setItems(result.getItems());
        } else {
          this.pageableResult.addItems(result.getItems());
        }
      })
      .catch(() => {
        this.pageableResult.setItems([]);
      })
      .finally(() => {
        this.loadingService.finishLoading(ref);
        this.storageSearchPreferences(filterText);
        this.filterText = "";
      });
  }

  // --- Permissões ---
  hasAccessToEditCompany(): boolean {
    return this.userService.getLoggedUser().hasPermission(UserPermissions.EditCompany);
  }

  // --- Getters & Utilitários ---
  hasCompany(): boolean {
    return Array.isArray(this.pageableResult.getItems()) && this.pageableResult.getItems().length > 0;
  }

  showScrollBottomBtn(): boolean {
    return this.pageableResult.getItems().length > 1;
  }

  showMoreCompaniesBtn(): boolean {
    return (
      this.pageableResult.getTotalItems() !== this.pageableResult.getItems().length &&
      this.pageableResult.getItems().length > 1
    );
  }

  storageSearchPreferences(filterText: string): void {
    this.userService.setUserPreferences(this.cleanFilterText(filterText));
    this.userProfile = this.userService.getUserProfile();
  }

  private cleanFilterText(text: string): string {
    return text ? text.replace(/[./-]/g, "") : "";
  }
}